<template>
	<div class="w-full relative flex flex-col items-center">
		<div
			class="relative w-full overflow-hidden"
			:style="{ height: `${slideshowHeight}px` }"
			@mouseenter="pauseSlideshow"
			@mouseleave="resumeSlideshow"
		>
			<div
				v-for="(review, index) in reviews"
				:key="`review-slideshow-${index}`"
				ref="reviewElements"
				class="absolute w-full transition-opacity duration-500 ease-in-out"
				:style="slideshowHeight > 0 ? { height: `${slideshowHeight}px` } : undefined"
				:class="{ 'opacity-100': index === currentSlide, 'opacity-0': index !== currentSlide }"
			>
				<div class="flex flex-col justify-center h-full gap-8 max-w-screen-sm mx-auto">
					<p class="text-paragraph-size-md text-center">
						<span>"{{ review.text }}"</span>
						<span class="mx-4 text-gray-400">—</span>
						<span class="text-gray-600">
							{{ review.reviewerName }}
						</span>
					</p>
					<div class="flex flex-col items-center gap-4">
						<MoleculeStars :rating="review.rating / 2" />
						<span class="text-title-size-sm font-bold text-gray-700">
							{{ review.rating / 2 }}/5
						</span>
					</div>
				</div>
			</div>
		</div>

		<div class="flex justify-center mt-8 gap-2">
			<button
				v-for="(_, index) in reviews"
				:key="`review-slideshow-dot-${index}`"
				:aria-label="`Go to slide ${index + 1}`"
				:class="index === currentSlide ? 'bg-brand-600' : 'bg-gray-300 hover:bg-gray-400'"
				class="w-3 h-3 rounded-full transition-colors duration-300 focus:outline-none"
				@click="setSlide(index)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { ReviewSlideType } from '#base/core/types/ReviewSlideType';

const slideInterval = ref<NodeJS.Timeout | string | number | undefined>();
const currentSlide = ref(0);
const reviewElements = ref<HTMLElement[]>([]);
const slideshowHeight = ref(0);
const resizeObserver = ref<ResizeObserver | undefined>();

const {
	reviews,
	autoplayInterval = 7000,
} = defineProps<{
	reviews: ReviewSlideType[];
	autoplayInterval?: number;
}>();

function setSlide(index: number) {
	currentSlide.value = index;
}

function setNextSlide() {
	if (currentSlide.value < reviews.length - 1) {
		currentSlide.value++;
	}
	else {
		currentSlide.value = 0;
	}
}

function calculateMaxHeight() {
	if (!reviewElements.value || reviewElements.value.length === 0) return;

	const heights = reviewElements.value.map(el => el.offsetHeight);
	slideshowHeight.value = Math.max(...heights);
}

function resetInterval() {
	clearInterval(slideInterval.value);
	slideInterval.value = undefined;
}

function pauseSlideshow() {
	if (slideInterval.value) {
		resetInterval();
	}
}

function resumeSlideshow() {
	if (autoplayInterval !== 0 && !slideInterval.value) {
		slideInterval.value = setInterval(() => {
			setNextSlide();
		}, autoplayInterval);
	}
}

onMounted(() => {
	resumeSlideshow();

	nextTick(() => {
		calculateMaxHeight();
	});

	resizeObserver.value = new ResizeObserver(calculateMaxHeight);

	if (reviewElements.value.length > 0) {
		const parent = reviewElements.value[0]?.parentElement;
		if (parent) {
			resizeObserver.value.observe(parent);
		}
	}
});

onUnmounted(() => {
	resetInterval();

	resizeObserver.value?.disconnect();
	resizeObserver.value = undefined;
});
</script>
